import React, { useState } from 'react';
import { Card, Button, Space, Collapse, Badge } from 'antd';
import { 
  SearchOutlined, 
  DownloadOutlined, 
  FileExcelOutlined, 
  EnvironmentOutlined,
  BankOutlined,
  ProjectOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ClearOutlined
} from '@ant-design/icons';
import LocationFilters from './LocationFilters';
import BusinessFilters from './BusinessFilters';
import StatusFilters from './StatusFilters';
import BankingFilters from './BankingFilters';
import TechnicalFilters from './TechnicalFilters';
import useMapFilters from '../hooks/useMapFilters';

const { Panel } = Collapse;

const FilterPanel = ({ onFilterChange, onSearch, onDownload, loading, darkMode, isMobile = false }) => {
  const { filters, updateFilter, resetFilters, getFilterSummary } = useMapFilters();
  const [activeKeys, setActiveKeys] = useState(isMobile ? [] : ['location', 'business']); 

  const handleFilterChange = (filterType, newValue) => {
    updateFilter(filterType, newValue);
    const updatedFilters = { ...filters, [filterType]: newValue };
    onFilterChange(updatedFilters);
  };

  const handleBatchChange = (patch) => {
    Object.entries(patch || {}).forEach(([key, value]) => {
      updateFilter(key, value);
    });
    const updatedFilters = { ...filters, ...patch };
    onFilterChange(updatedFilters);
  };

  const handleCollapseChange = (keys) => {
    setActiveKeys(keys);
  };

  const getActiveFilterCount = (filterType) => {
    let count = 0;
    switch (filterType) {
      case 'location':
        if (filters.provinceId) count++;
        if (filters.districtId) count++;
        if (filters.searchText) count++;
        break;
      case 'business':
        if (filters.firmIds?.length > 0) count++;
        if (filters.activeProjectIds?.length > 0) count++;
        if (filters.passiveProjectIds?.length > 0) count++;
        if (filters.categoryIds?.length > 0) count++;
        if (filters.dateFrom || filters.dateTo) count++;
        break;
      case 'status':
        if (filters.statusIds?.length > 0) count++;
        if (filters.notificationIds?.length > 0) count++;
        break;
      case 'banking':
        if (filters.includedBanks?.length > 0) count++;
        if (filters.excludedBanks?.length > 0) count++;
        if (filters.btmAtmOnsite?.length > 0) count++;
        if (filters.btmAtmOffsite?.length > 0) count++;
        break;
      case 'technical':
        if (filters.segment?.length > 0) count++;
        break;
    }
    return count;
  };

  const handleReset = () => {
    resetFilters();
    const resetFilterState = {
      provinceId: null,
      districtId: null,
      searchText: '',
      firmIds: [],
      activeProjectIds: [],
      passiveProjectIds: [],
      categoryIds: [],
      statusIds: [],
      notificationIds: [],
      includedBanks: [],
      excludedBanks: [],
      btmAtmOnsite: [],
      btmAtmOffsite: [],
      segment: null,
    };
    onFilterChange(resetFilterState);
  };

  return (
    <Card 
      className="filter-panel"
      bodyStyle={{ padding: isMobile ? '8px' : 0, margin: 0 }}
      style={{ 
        background: darkMode ? 'var(--dark-card-bg)' : 'var(--color-bg)',
        color: darkMode ? 'var(--dark-text)' : 'var(--color-text)',
        margin: 0,
        padding: 0
      }}
      size="small"
    >
      <Collapse 
        activeKey={activeKeys}
        onChange={handleCollapseChange}
        size="small"
        ghost={darkMode}
        style={{ background: 'transparent' }}
      >
        <Panel 
          header={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: isMobile ? '13px' : '14px' }}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <EnvironmentOutlined style={{ marginRight: isMobile ? 6 : 8, fontSize: isMobile ? '12px' : '14px' }} />
                Konum
              </span>
              {getActiveFilterCount('location') > 0 && (
                <Badge count={getActiveFilterCount('location')} size="small" />
              )}
            </div>
          }
          key="location"
        >
          <LocationFilters 
            filters={filters} 
            onChange={handleFilterChange}
            onBatchChange={handleBatchChange}
            darkMode={darkMode}
          />
        </Panel>
        
        <Panel 
          header={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: isMobile ? '13px' : '14px' }}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <ProjectOutlined style={{ marginRight: isMobile ? 6 : 8, fontSize: isMobile ? '12px' : '14px' }} />
                İş
              </span>
              {getActiveFilterCount('business') > 0 && (
                <Badge count={getActiveFilterCount('business')} size="small" />
              )}
            </div>
          }
          key="business"
        >
          <BusinessFilters 
            filters={filters} 
            onChange={handleFilterChange} 
            darkMode={darkMode}
          />
        </Panel>
        
        <Panel 
          header={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: isMobile ? '13px' : '14px' }}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleOutlined style={{ marginRight: isMobile ? 6 : 8, fontSize: isMobile ? '12px' : '14px' }} />
                Durum
              </span>
              {getActiveFilterCount('status') > 0 && (
                <Badge count={getActiveFilterCount('status')} size="small" />
              )}
            </div>
          }
          key="status"
        >
          <StatusFilters 
            filters={filters} 
            onChange={handleFilterChange} 
            darkMode={darkMode}
          />
        </Panel>
        
        <Panel 
          header={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: isMobile ? '13px' : '14px' }}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <BankOutlined style={{ marginRight: isMobile ? 6 : 8, fontSize: isMobile ? '12px' : '14px' }} />
                Banka
              </span>
              {getActiveFilterCount('banking') > 0 && (
                <Badge count={getActiveFilterCount('banking')} size="small" />
              )}
            </div>
          }
          key="banking"
        >
          <BankingFilters 
            filters={filters} 
            onChange={handleFilterChange} 
            darkMode={darkMode}
          />
        </Panel>
        
        <Panel 
          header={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: isMobile ? '13px' : '14px' }}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <SettingOutlined style={{ marginRight: isMobile ? 6 : 8, fontSize: isMobile ? '12px' : '14px' }} />
                Teknik
              </span>
              {getActiveFilterCount('technical') > 0 && (
                <Badge count={getActiveFilterCount('technical')} size="small" />
              )}
            </div>
          }
          key="technical"
        >
          <TechnicalFilters 
            filters={filters} 
            onChange={handleFilterChange}
            darkMode={darkMode}
          />
        </Panel>
      </Collapse>
      
      <div style={{ marginTop: isMobile ? '8px' : '12px', padding: isMobile ? '0 4px' : '0' }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Button 
            type="primary" 
            icon={<SearchOutlined />} 
            onClick={onSearch} 
            loading={loading}
            block
            size={isMobile ? 'middle' : 'small'}
            style={{ fontSize: isMobile ? '13px' : '14px' }}
          >
            {isMobile ? 'Ara' : `Uygula (${getFilterSummary()})`}
          </Button>
          
          <Space.Compact style={{ width: '100%' }}>
            <Button 
              icon={<ClearOutlined />}
              onClick={handleReset}
              size={isMobile ? 'middle' : 'small'}
              style={{ flex: 1, fontSize: isMobile ? '11px' : '12px' }}
            >
              {isMobile ? '✕' : 'Temizle'}
            </Button>
            <Button 
              onClick={() => onDownload('excel')} 
              icon={<FileExcelOutlined />}
              size={isMobile ? 'middle' : 'small'}
              style={{ flex: 1, fontSize: isMobile ? '11px' : '12px' }}
            >
              {isMobile ? 'XLS' : 'Excel'}
            </Button>
            <Button 
              onClick={() => onDownload('kmz')} 
              icon={<DownloadOutlined />}
              size={isMobile ? 'middle' : 'small'}
              style={{ flex: 1, fontSize: isMobile ? '11px' : '12px' }}
            >
              KMZ
            </Button>
          </Space.Compact>
        </Space>
      </div>
    </Card>
  );
};

export default FilterPanel;
